// @ts-nocheck

import { uniq, each } from 'lodash';
import React, { useEffect, useMemo, useRef, useCallback, useState, useLayoutEffect } from 'react';
import styled, { css } from 'styled-components';

import { SpineAnimations } from '../../constants/animations';
import { usePrevious } from '../../hooks';
import KLSoundButton from '../../modules/koh-lanta/components/common/KLSoundButton';
import SoundPlayer from '../../singletons/SoundPlayer';
import type { SpineAnimationId } from '../../types/animations';
import type { SoundId } from '../../types/sounds';
import SpineCanvas from '../SpineCanvas';

const DEFAULT_ANIMATION_RATIO = 400 / 380;

const Wrapper = styled.div`
  position: absolute;
  width: 100%;
  left: 0;

  ${({ $ratio }) => css`
    padding-top: calc(100% * (${$ratio || DEFAULT_ANIMATION_RATIO}));
  `}
`;

const Canvas = styled.canvas`
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  position: absolute;
`;

type SpinePlayerProps = {
  className?: string;
  animationId: SpineAnimationId;
  soundId?: SoundId;
  scale?: number;
  height?: number;
  width?: number;
  onStartAnimation?: () => void;
  onEndAnimation?: () => void;
  hideMuteButton?: boolean;
  preventCutAnim?: boolean;
};

const SpinePlayer = ({
  className,
  animationId,
  soundId,
  scale,
  height,
  width,
  onStartAnimation,
  onEndAnimation,
  hideMuteButton,
  preventCutAnim,
}: SpinePlayerProps) => {
  const spineCanvas = useRef();
  const canvas = useRef();
  const [sound, setSound] = useState(null);
  const soundRef = useRef(null);
  useEffect(() => {
    soundRef.current = sound;
  }, [sound]);
  const shouldPlaySound = useRef(true);
  const loadingSound = useRef(false);
  const isAnimationPlaying = useRef(false);

  const wrapperElement = useRef();
  const [wrapperDimension, setWrapperDimension] = useState({ width: 1, height: 1 });
  const previousAnimationId = usePrevious(animationId);
  const reqAnimFrameRef = useRef();

  useLayoutEffect(() => {
    const wrapperNode = wrapperElement.current;

    if (wrapperNode) {
      const { offsetWidth: width, offsetHeight: height } = wrapperNode;

      if (width && height) {
        setWrapperDimension({
          width,
          height,
        });
      }
    }
  }, []);

  const updateCanvas = useCallback(() => {
    const sc = spineCanvas.current ?? {};

    if (sc.isAvailable?.()) {
      sc.updateTime();
      sc.render();

      reqAnimFrameRef.current = requestAnimationFrame(() => updateCanvas());
    }
  }, []);

  useEffect(() => {
    return () => {
      if (reqAnimFrameRef.current) {
        cancelAnimationFrame(reqAnimFrameRef.current);
      }
    };
  }, []);

  const animations = useMemo(() => SpineAnimations[animationId], [animationId]);

  useEffect(() => {
    const sc = new SpineCanvas(canvas.current);
    spineCanvas.current = sc;

    return () => {
      if (sc) {
        sc.deleteAnimations();
        spineCanvas.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const sc = spineCanvas.current;

    if (sc) {
      sc.resize(height, width);
    }
  }, [height, width]);

  useEffect(() => {
    return () => {
      shouldPlaySound.current = false;

      if (soundRef.current) {
        soundRef.current.stop();
      }
    };
  }, []);

  const startAnimation = useCallback(
    (animations, index) => {
      const sc = spineCanvas.current;

      if (!sc || !animations || !animations[index]) {
        onEndAnimation?.();
        isAnimationPlaying.current = false;
      } else {
        const { assets, animation, loop, forwards, skin } = animations[index];

        const callback = () => {
          startAnimation(animations, index + 1);
        };

        if (previousAnimationId) {
          sc.deleteAnimations();
        }

        sc.playAnimation(assets.id, animation, skin, loop, false, forwards, callback);
      }
    },
    [onEndAnimation, previousAnimationId],
  );

  useEffect(() => {
    const sc = spineCanvas.current ?? {};
    const currentSound = soundRef.current;

    if (sc.isAvailable?.() && (!preventCutAnim || !isAnimationPlaying.current)) {
      const uniqAnimations = uniq(animations, (a) => a?.assets?.id).filter((k) => k);

      if (soundId && !loadingSound.current) {
        if (currentSound) {
          currentSound.stop();
        }

        loadingSound.current = true;

        SoundPlayer.load(soundId).then((s) => {
          setSound(s);

          loadingSound.current = false;
        });
      }

      if (uniqAnimations?.length > 0 && (!soundId || sound)) {
        sc.loadAnimations(uniqAnimations)
          .then(() => {
            const scaleX = scale ?? 1;
            const scaleY = scaleX * (wrapperDimension.width / wrapperDimension.height) * DEFAULT_ANIMATION_RATIO;

            each(uniqAnimations, (a) => {
              sc.setElement(a.assets.id, 0, 0, scaleX, scaleY);
            });

            onStartAnimation?.();
            startAnimation(animations, 0);

            sound?.play();
            isAnimationPlaying.current = true;
          })
          .catch(() => {});
      }

      updateCanvas();
    }
  }, [
    animations,
    soundId,
    scale,
    sound,
    startAnimation,
    updateCanvas,
    wrapperDimension,
    onStartAnimation,
    preventCutAnim,
  ]);

  return (
    <>
      {sound && !hideMuteButton ? <KLSoundButton sound={sound} /> : undefined}
      <Wrapper ref={wrapperElement} className={className} $ratio={height && width ? height / width : undefined}>
        <Canvas className="canvas" ref={canvas} />
      </Wrapper>
    </>
  );
};

export default SpinePlayer;
